<template>
  <div
    :class="[$style.container, { [$style.showBg]: title || localLink.name }]"
  >
    <div v-if="title" :class="$style.title">
      <h2>
        <span :class="$style.accent">
          <NsIcon :class="$style.accentIcon" icon-name="icon-spark-left-24" />
        </span>
        {{ title }}
      </h2>
    </div>
    <div :class="$style.productList">
      <NsProductCard
        v-for="sku in limitedSkus"
        :key="sku"
        :class="$style.productCard"
        :sku="sku"
        :hide-favorites="hideFavorites"
        :forcesv="forcesv"
        @product-update="handleProductUpdate"
      />
    </div>
    <div v-if="!hideLoadMore" :class="$style.loadMore">
      <button class="primary-solid" @click="loadMoreSkus">
        {{ loadMoreText }}
      </button>
    </div>
    <div v-if="localLink.name" :class="$style.link">
      <NsExpressiveLink :href="localLink.href">{{
        localLink.name
      }}</NsExpressiveLink>
    </div>
  </div>
</template>

<script>
import { NsExpressiveLink, NsIcon } from "@nuskin/design-components";
import { StringService } from "@nuskin/ns-util";

import NsProductCard from "./NsProductCard.vue";
import { fromJsonString } from "../utils/helpers";

/**
 * NsProductList
 *
 * Events
 * @event product-update This event fires when a product card's data is updated.
 * Event emits the product: (product) => {}
 */

export default {
  name: "NsProductList",
  components: { NsExpressiveLink, NsProductCard, NsIcon },
  props: {
    /**
     * Force the Product Cards to show points
     */
    forcesv: Boolean,
    /**
     * Hide Favorite Button on Product Cards
     */
    hideFavorites: Boolean,
    /**
     * Array of skus for product list. Takes array of strings or objects ([sku] || [{ sku }])
     */
    items: {
      type: [String, Array],
      default: () => []
    },
    /**
     * Link object ({ name, href })
     */
    link: {
      type: [String, Object],
      default: () => ({})
    },
    /**
     * Title text
     */
    title: {
      type: String,
      default: ""
    },
    /**
     * Initial number of products displayed and number added per "Load More" button click. Set to 0 to disable.
     */
    skusPerLoad: {
      type: Number,
      default: 20
    },
    translations: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      skusLimit: this.skusPerLoad,
      loadMoreText: "Load More"
    };
  },
  computed: {
    localLink() {
      return fromJsonString(this.link, {});
    },
    skus() {
      const value = fromJsonString(this.items, []);
      return value.map(item => {
        if (typeof item === "object") {
          return item.sku || item.SKU;
        }
        return item;
      });
    },
    limitedSkus() {
      if (this.skusLimit > 0) {
        return this.skus.slice(0, this.skusLimit);
      }
      return this.skus;
    },
    hideLoadMore() {
      return !this.skusLimit || this.skusLimit >= this.skus.length;
    }
  },
  async mounted() {
    await this.setCommonStrings();
  },
  methods: {
    handleProductUpdate(product) {
      this.$emit("product-update", product);
    },
    loadMoreSkus(event) {
      event.target.blur();
      this.skusLimit += this.skusPerLoad;
    },
    async setCommonStrings() {
      if (!this.translations || !this.translations.loadMore) {
        const commonStringKeys = [{ key: "loadMore", dflt: this.loadMoreText }];

        await this.$nextTick();

        try {
          const commonStrings = await StringService.getStrings(
            commonStringKeys
          );
          if (commonStrings && commonStrings.loadMore) {
            this.loadMoreText = commonStrings.loadMore;
          }
        } catch (err) {
          console.warn("Unable to retrieve common translations.", err);
        }
      }
    }
  }
};
</script>

<style lang="scss" module>
@use "sass:map";
@use "~@nuskin/ns-core-styles/src/scss/global.mixins";
@use '~@nuskin/ns-core-styles/src/scss/typographyVariables';

.container,
.container * {
  box-sizing: border-box;
  -webkit-font-smoothing: antialiased;
  font-family: map.get(typographyVariables.$typography, font-family-primary);
}

.container {
  &.showBg {
    background-color: rgb(246, 247, 247);
    padding-bottom: 15px;
  }
}

.productList {
  display: grid;
  grid-template-columns: 100%;
  grid-gap: 5px;
  justify-content: center;
  justify-items: center;
  padding: 255px 0;
  margin: -245px auto;
  width: 100%;
  max-width: 1440px;
  background-color: rgb(246, 247, 247);
}

.title {
  padding: 30px 0 20px;
  text-align: center;

  h2 {
    display: inline-block;
    color: #272525;
    font-family: map.get(typographyVariables.$typography, font-family-primary);
    font-size: 20px;
    font-style: italic;
    font-weight: bold;
    text-transform: uppercase;
    position: relative;
    margin-top: 0;
    margin-bottom: 0;
    text-align: center;
    letter-spacing: 1.5px;
    line-height: 34px;
  }

  .accent {
    position: absolute;
    left: -20px;
    top: 2px;
  }

  .accentIcon {
    width: 25px;
    height: 25px;
  }
}

// nested for specificity
.productList .productCard {
  margin: 0 auto;
  width: 100%;
  max-width: 400px;
}

.link {
  padding: 20px 0;
  text-align: center;
}

.loadMore {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 50px 0 35px;
}

@media (min-width: 768px) {
  .productList {
    grid-template-columns: repeat(auto-fill, 315px);
    grid-gap: 15px;
    padding: 245px 0;
    background-color: inherit;
  }

  .title {
    padding: 60px 0 30px;
  }

  .link {
    padding: 50px 0 40px;
  }
}
</style>
